import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import Box from '../../components/Common/Box';
import Loader from '../../components/Common/Loader';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';

interface IFundamental {
    societe_symbole: string;
    exercice: number;
    period_type: string;
    closing_date: string | null;
    revenue: number | null;
    ebitda: number | null;
    ebit: number | null;
    net_income: number | null;
    net_income_reported: number | null;
    equity: number | null;
    financial_debt: number | null;
    cash: number | null;
    net_debt: number | null;
    commentaires: string | null;
    created_at: string;
    updated_at: string;
}

interface Stock {
    symbole: string;
    nom_societe: string;
    secteur: string;
}

const UNIT_MULTIPLIERS = [
    { value: 1, label: 'Unités FCFA' },
    { value: 1000, label: 'Milliers FCFA (K)' },
    { value: 1000000, label: 'Millions FCFA (M)' },
    { value: 1000000000, label: 'Milliards FCFA (Mds)' }
];

const PERIOD_TYPES = [
    // Cumulative (YTD - Year To Date)
    { value: 'ANNUAL', label: 'Annuel (12 mois)' },
    { value: 'Q1', label: 'Q1 - Trimestre 1 (3 mois: Jan-Mar)' },
    { value: 'S1', label: 'S1 - Semestre 1 (6 mois: Jan-Jun)' },
    { value: '9M', label: '9M - 9 premiers mois (Jan-Sep)' },
    // Standalone quarters (for companies reporting individual quarters after S1)
    { value: 'Q2', label: 'Q2 seul - Trimestre 2 (3 mois: Apr-Jun)' },
    { value: 'Q3', label: 'Q3 seul - Trimestre 3 (3 mois: Jul-Sep)' },
    { value: 'Q4', label: 'Q4 seul - Trimestre 4 (3 mois: Oct-Dec)' }
];

// Generate fiscal year options (current year back to 2015)
const currentYear = new Date().getFullYear();
const FISCAL_YEARS = Array.from({ length: currentYear - 2015 + 1 }, (_, i) => currentYear - i);

const AdminFundamentalsDetailScreen: React.FC = () => {
    const { symbole } = useParams<{ symbole: string }>();
    const navigate = useNavigate();
    const { token } = useAuth();
    const [fundamentals, setFundamentals] = useState<IFundamental[]>([]);
    const [stock, setStock] = useState<Stock | null>(null);
    const [loading, setLoading] = useState(true);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFundamental, setEditingFundamental] = useState<IFundamental | null>(null);
    const [unitMultiplier, setUnitMultiplier] = useState(1000000); // Default: Millions
    const [formData, setFormData] = useState({
        exercice: currentYear,
        period_type: 'ANNUAL',
        revenue: '',
        ebitda: '',
        ebit: '',
        net_income: '',
        net_income_reported: '',
        equity: '',
        financial_debt: '',
        cash: '',
        commentaires: ''
    });

    useEffect(() => {
        if (token && symbole) {
            fetchData();
        }
    }, [token, symbole]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch fundamentals history
            const fundResponse = await fetch(`${API_URL}/v1/admin/fundamentals/${symbole}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (fundResponse.ok) {
                const data = await fundResponse.json();
                setFundamentals(data);
            }

            // Fetch stock details
            const stockResponse = await fetch(`${API_URL}/v1/stocks/${symbole}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (stockResponse.ok) {
                const data = await stockResponse.json();
                setStock(data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setLoading(false);
    };

    const formatCompact = (n: number | null) => {
        if (n === null || n === undefined) return '—';
        const abs = Math.abs(n);
        if (abs >= 1000000000) {
            return (n / 1000000000).toFixed(1) + ' Mds';
        } else if (abs >= 1000000) {
            return (n / 1000000).toFixed(1) + ' M';
        } else if (abs >= 1000) {
            return (n / 1000).toFixed(1) + ' K';
        }
        return n.toLocaleString('fr-FR');
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('fr-FR');
    };

    const toRealValue = (value: string): number | null => {
        if (!value || value.trim() === '') return null;
        const num = parseFloat(value);
        if (isNaN(num)) return null;
        return Math.round(num * unitMultiplier);
    };

    const fromRealValue = (value: number | null): string => {
        if (value === null || value === undefined) return '';
        return (value / unitMultiplier).toString();
    };

    // Rescale all numeric form fields when unit changes (prevents corruption)
    const handleUnitChange = (newMultiplier: number) => {
        if (newMultiplier === unitMultiplier) return;
        const rescale = (val: string): string => {
            if (!val || val.trim() === '') return val;
            const num = parseFloat(val);
            if (isNaN(num)) return val;
            return (num * unitMultiplier / newMultiplier).toString();
        };
        setFormData(prev => ({
            ...prev,
            revenue: rescale(prev.revenue),
            ebitda: rescale(prev.ebitda),
            ebit: rescale(prev.ebit),
            net_income: rescale(prev.net_income),
            net_income_reported: rescale(prev.net_income_reported),
            equity: rescale(prev.equity),
            financial_debt: rescale(prev.financial_debt),
            cash: rescale(prev.cash),
        }));
        setUnitMultiplier(newMultiplier);
    };

    const handleDelete = async (f: IFundamental) => {
        if (!window.confirm('Supprimer cette entrée ?')) return;
        try {
            // Use composite key for delete
            const response = await fetch(`${API_URL}/v1/admin/fundamentals/${f.societe_symbole}/${f.exercice}/${f.period_type}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.ok) {
                fetchData();
            }
        } catch (error) {
            console.error('Error deleting:', error);
        }
    };

    const openEditModal = (fundamental: IFundamental | null) => {
        if (fundamental) {
            setEditingFundamental(fundamental);
            setFormData({
                exercice: fundamental.exercice,
                period_type: fundamental.period_type,
                revenue: fromRealValue(fundamental.revenue),
                ebitda: fromRealValue(fundamental.ebitda),
                ebit: fromRealValue(fundamental.ebit),
                net_income: fromRealValue(fundamental.net_income),
                net_income_reported: fromRealValue(fundamental.net_income_reported),
                equity: fromRealValue(fundamental.equity),
                financial_debt: fromRealValue(fundamental.financial_debt),
                cash: fromRealValue(fundamental.cash),
                commentaires: fundamental.commentaires || ''
            });
        } else {
            setEditingFundamental(null);
            setFormData({
                exercice: currentYear,
                period_type: 'ANNUAL',
                revenue: '',
                ebitda: '',
                ebit: '',
                net_income: '',
                net_income_reported: '',
                equity: '',
                financial_debt: '',
                cash: '',
                commentaires: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const payload = {
                societe_symbole: symbole,
                exercice: formData.exercice,
                period_type: formData.period_type,
                revenue: toRealValue(formData.revenue),
                ebitda: toRealValue(formData.ebitda),
                ebit: toRealValue(formData.ebit),
                net_income: toRealValue(formData.net_income),
                net_income_reported: toRealValue(formData.net_income_reported),
                equity: toRealValue(formData.equity),
                financial_debt: toRealValue(formData.financial_debt),
                cash: toRealValue(formData.cash),
                commentaires: formData.commentaires
            };

            // Check if we're editing AND if the composite key (exercice or period_type) changed
            const pkChanged = editingFundamental && (
                editingFundamental.exercice !== formData.exercice ||
                editingFundamental.period_type !== formData.period_type
            );

            if (pkChanged && editingFundamental) {
                // Use atomic replace endpoint (DELETE + INSERT in single transaction)
                const replaceUrl = `${API_URL}/v1/admin/fundamentals/replace?old_symbole=${editingFundamental.societe_symbole}&old_exercice=${editingFundamental.exercice}&old_period_type=${editingFundamental.period_type}`;
                const replaceResponse = await fetch(replaceUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                });

                if (replaceResponse.ok) {
                    setIsModalOpen(false);
                    fetchData();
                } else {
                    const errorData = await replaceResponse.json();
                    const msg = typeof errorData.detail === 'string'
                        ? errorData.detail
                        : Array.isArray(errorData.detail)
                            ? errorData.detail.map((e: any) => `${e.loc?.join('.')} — ${e.msg}`).join('\n')
                            : JSON.stringify(errorData);
                    alert(`Erreur : ${msg}`);
                }
            } else {
                // Standard create or update (no PK change)
                const url = editingFundamental
                    ? `${API_URL}/v1/admin/fundamentals/${editingFundamental.societe_symbole}/${editingFundamental.exercice}/${editingFundamental.period_type}`
                    : `${API_URL}/v1/admin/fundamentals`;

                const response = await fetch(url, {
                    method: editingFundamental ? 'PUT' : 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify(payload)
                });

                if (response.ok) {
                    setIsModalOpen(false);
                    fetchData();
                } else {
                    const errorData = await response.json();
                    const msg = typeof errorData.detail === 'string'
                        ? errorData.detail
                        : Array.isArray(errorData.detail)
                            ? errorData.detail.map((e: any) => `${e.loc?.join('.')} — ${e.msg}`).join('\n')
                            : JSON.stringify(errorData);
                    alert(`Erreur : ${msg}`);
                }
            }
        } catch (error) {
            console.error('Error saving:', error);
            alert('Erreur réseau ou réponse invalide. Vérifiez la console.');
        }
    };

    // Calculate net debt in real-time for display
    const calculatedNetDebt = (() => {
        const debt = toRealValue(formData.financial_debt);
        const cash = toRealValue(formData.cash);
        if (debt === null || cash === null) return null;
        return debt - cash;
    })();

    const cellStyle: React.CSSProperties = {
        padding: '12px 16px',
        textAlign: 'right',
        fontSize: '13px',
        borderBottom: '1px solid #e5e7eb'
    };

    const headerCellStyle: React.CSSProperties = {
        ...cellStyle,
        background: '#f9fafb',
        fontWeight: 600,
        color: '#374151',
        position: 'sticky',
        top: 0
    };

    const inputStyle: React.CSSProperties = {
        width: '100%',
        padding: '10px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        fontSize: '14px'
    };

    return (
        <SiteLayout>
            <Header icon="analytics" title={`Historique Fondamentales - ${symbole}`} />

            <div style={{ padding: '20px' }}>
                {/* Back button and stock info */}
                <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <button
                            onClick={() => navigate('/admin/fundamentals')}
                            style={{
                                background: '#f3f4f6',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            ← Retour à la liste
                        </button>
                        {stock && (
                            <div>
                                <span style={{ fontWeight: 'bold', fontSize: '18px' }}>{stock.symbole}</span>
                                <span style={{ color: '#6b7280', marginLeft: '8px' }}>{stock.nom_societe}</span>
                                <span style={{
                                    background: '#e0e7ff',
                                    color: '#3730a3',
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    marginLeft: '8px'
                                }}>
                                    {stock.secteur}
                                </span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => openEditModal(null)}
                        style={{
                            background: '#2563eb',
                            color: '#fff',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: 500
                        }}
                    >
                        ➕ Ajouter une période
                    </button>
                </div>

                <Box>
                    <h2 style={{ margin: '0 0 20px 0', fontSize: '16px' }}>
                        📊 Historique des données fondamentales ({fundamentals.length} entrées)
                    </h2>

                    {loading ? (
                        <Loader message="Chargement de l'historique..." />
                    ) : fundamentals.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                            <p>Aucune donnée fondamentale pour cette société.</p>
                            <button
                                onClick={() => openEditModal(null)}
                                style={{
                                    marginTop: '16px',
                                    background: '#2563eb',
                                    color: '#fff',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                Ajouter une entrée
                            </button>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...headerCellStyle, textAlign: 'left' }}>Période</th>
                                        <th style={{ ...headerCellStyle, textAlign: 'center' }}>Exercice</th>
                                        <th style={headerCellStyle}>CA</th>
                                        <th style={headerCellStyle}>EBITDA</th>
                                        <th style={headerCellStyle}>EBIT</th>
                                        <th style={headerCellStyle}>Résultat Net</th>
                                        <th style={headerCellStyle}>RN (Reporté)</th>
                                        <th style={headerCellStyle}>Capitaux Propres</th>
                                        <th style={headerCellStyle}>Dettes Fin.</th>
                                        <th style={headerCellStyle}>Trésorerie</th>
                                        <th style={headerCellStyle}>Dette Nette</th>
                                        <th style={{ ...headerCellStyle, textAlign: 'center' }}>MAJ</th>
                                        <th style={{ ...headerCellStyle, textAlign: 'center' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fundamentals.map((f, idx) => (
                                        <tr key={`${f.societe_symbole}-${f.exercice}-${f.period_type}`} style={{ background: idx % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                            <td style={{ ...cellStyle, textAlign: 'left', fontWeight: 500 }}>
                                                {f.period_type}
                                            </td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                {f.exercice || (f.closing_date ? new Date(f.closing_date).getFullYear() : '—')}
                                            </td>
                                            <td style={cellStyle}>{formatCompact(f.revenue)}</td>
                                            <td style={cellStyle}>{formatCompact(f.ebitda)}</td>
                                            <td style={cellStyle}>{formatCompact(f.ebit)}</td>
                                            <td style={{
                                                ...cellStyle,
                                                color: f.net_income !== null && f.net_income < 0 ? '#dc2626' : '#16a34a',
                                                fontWeight: 500
                                            }}>
                                                {formatCompact(f.net_income)}
                                            </td>
                                            <td style={cellStyle}>{formatCompact(f.net_income_reported)}</td>
                                            <td style={cellStyle}>{formatCompact(f.equity)}</td>
                                            <td style={cellStyle}>{formatCompact(f.financial_debt)}</td>
                                            <td style={cellStyle}>{formatCompact(f.cash)}</td>
                                            <td style={{
                                                ...cellStyle,
                                                color: f.net_debt !== null && f.net_debt < 0 ? '#16a34a' : '#dc2626',
                                                fontWeight: 500
                                            }}>
                                                {formatCompact(f.net_debt)}
                                                {f.net_debt !== null && f.net_debt < 0 && (
                                                    <span style={{ fontSize: '10px', marginLeft: '4px' }}>✅</span>
                                                )}
                                            </td>
                                            <td style={{ ...cellStyle, textAlign: 'center', fontSize: '11px', color: '#6b7280' }}>
                                                {formatDate(f.updated_at)}
                                            </td>
                                            <td style={{ ...cellStyle, textAlign: 'center' }}>
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                                    <button
                                                        onClick={() => openEditModal(f)}
                                                        title="Modifier"
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(f)}
                                                        title="Supprimer"
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            cursor: 'pointer',
                                                            fontSize: '14px'
                                                        }}
                                                    >
                                                        🗑
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Legend */}
                    {fundamentals.length > 0 && (
                        <div style={{ marginTop: '16px', fontSize: '11px', color: '#6b7280' }}>
                            <strong>Légende :</strong> Les montants sont en FCFA.
                            <span style={{ color: '#16a34a', marginLeft: '8px' }}>✅ Vert</span> = Cash net (trésorerie &gt; dettes)
                            <span style={{ color: '#dc2626', marginLeft: '8px' }}>Rouge</span> = Résultat négatif ou dette nette positive
                        </div>
                    )}
                </Box>
            </div>

            {/* Modal for editing/adding */}
            {isModalOpen && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff',
                        borderRadius: '12px',
                        padding: '24px',
                        width: '90%',
                        maxWidth: '600px',
                        maxHeight: '90vh',
                        overflow: 'auto'
                    }}>
                        <h2 style={{ margin: '0 0 20px 0' }}>
                            {editingFundamental ? '✏️ Modifier les données' : '➕ Ajouter une période'}
                        </h2>

                        {/* Unit Selector */}
                        <div style={{ marginBottom: '20px', background: '#f3f4f6', padding: '12px', borderRadius: '8px' }}>
                            <label style={{ fontWeight: 500, fontSize: '14px' }}>Unité de saisie :</label>
                            <select
                                value={unitMultiplier}
                                onChange={(e) => handleUnitChange(parseInt(e.target.value))}
                                style={{ ...inputStyle, marginTop: '8px' }}
                            >
                                {UNIT_MULTIPLIERS.map(u => (
                                    <option key={u.value} value={u.value}>{u.label}</option>
                                ))}
                            </select>
                            <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#6b7280' }}>
                                ℹ️ L'unité est une aide à la saisie. Les données sont toujours enregistrées et consultées en FCFA.
                            </p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>Exercice Fiscal</label>
                                <select
                                    value={formData.exercice}
                                    onChange={(e) => setFormData({ ...formData, exercice: parseInt(e.target.value) })}
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                >
                                    {FISCAL_YEARS.map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>Type de période</label>
                                <select
                                    value={formData.period_type}
                                    onChange={(e) => setFormData({ ...formData, period_type: e.target.value })}
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                >
                                    {PERIOD_TYPES.map(p => (
                                        <option key={p.value} value={p.value}>{p.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>Chiffre d'Affaires</label>
                                <input
                                    type="number"
                                    value={formData.revenue}
                                    onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
                                    placeholder="CA"
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>EBITDA</label>
                                <input
                                    type="number"
                                    value={formData.ebitda}
                                    onChange={(e) => setFormData({ ...formData, ebitda: e.target.value })}
                                    placeholder="EBITDA"
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>EBIT (Rés. Exploitation)</label>
                                <input
                                    type="number"
                                    value={formData.ebit}
                                    onChange={(e) => setFormData({ ...formData, ebit: e.target.value })}
                                    placeholder="EBIT"
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>Résultat Net</label>
                                <input
                                    type="number"
                                    value={formData.net_income}
                                    onChange={(e) => setFormData({ ...formData, net_income: e.target.value })}
                                    placeholder="Net Income"
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>RN Reporté (Part du grp / HAO...)</label>
                                <input
                                    type="number"
                                    value={formData.net_income_reported}
                                    onChange={(e) => setFormData({ ...formData, net_income_reported: e.target.value })}
                                    placeholder="Reported"
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>Capitaux Propres</label>
                                <input
                                    type="number"
                                    value={formData.equity}
                                    onChange={(e) => setFormData({ ...formData, equity: e.target.value })}
                                    placeholder="Equity"
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>Dettes Financières</label>
                                <input
                                    type="number"
                                    value={formData.financial_debt}
                                    onChange={(e) => setFormData({ ...formData, financial_debt: e.target.value })}
                                    placeholder="Dettes"
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ fontWeight: 500, fontSize: '14px' }}>Trésorerie</label>
                                <input
                                    type="number"
                                    value={formData.cash}
                                    onChange={(e) => setFormData({ ...formData, cash: e.target.value })}
                                    placeholder="Cash"
                                    style={{ ...inputStyle, marginTop: '4px' }}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: '16px' }}>
                            <label style={{ fontWeight: 500, fontSize: '14px' }}>Commentaires / Notes</label>
                            <textarea
                                value={formData.commentaires}
                                onChange={(e) => setFormData({ ...formData, commentaires: e.target.value })}
                                placeholder="Détails sur la consolidation, éléments exceptionnels, etc."
                                style={{ ...inputStyle, marginTop: '4px', height: '80px', resize: 'vertical' }}
                            />
                        </div>

                        {/* Net Debt Preview */}
                        <div style={{
                            marginTop: '16px',
                            padding: '12px',
                            background: calculatedNetDebt !== null && calculatedNetDebt < 0 ? '#dcfce7' : '#fef2f2',
                            borderRadius: '8px'
                        }}>
                            <strong>Dette Nette calculée :</strong> {calculatedNetDebt !== null ? formatCompact(calculatedNetDebt) + ' FCFA' : '—'}
                            {calculatedNetDebt !== null && calculatedNetDebt < 0 && (
                                <span style={{ marginLeft: '8px', color: '#16a34a' }}>✅ Cash net positif</span>
                            )}
                        </div>

                        {/* Actions */}
                        <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setIsModalOpen(false)}
                                style={{
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    border: '1px solid #d1d5db',
                                    background: '#fff',
                                    cursor: 'pointer'
                                }}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSave}
                                style={{
                                    padding: '10px 20px',
                                    borderRadius: '6px',
                                    border: 'none',
                                    background: '#2563eb',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontWeight: 500
                                }}
                            >
                                {editingFundamental ? 'Enregistrer les modifications' : 'Ajouter'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </SiteLayout>
    );
};

export default AdminFundamentalsDetailScreen;
